// Project Communications — per-job email capture panel (office roles only).
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CommViewer } from "@/components/communications/CommViewer";
import { COMM_TYPE_FILTERS as TYPE_FILTERS } from "@/components/communications/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Copy, Search, Paperclip, ShieldAlert, X,
} from "lucide-react";
import { toast } from "sonner";


const POLICIES = [
  ["contacts_users", "Project contacts + company users"],
  ["company_users", "Company users only"],
  ["approved_only", "Approved senders / domains"],
  ["anyone", "Anyone with the address"],
];




const dayKey = (iso) => (iso || "").slice(0, 10);
const dayLabel = (k) => {
  const today = new Date().toISOString().slice(0, 10);
  const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (k === today) return "Today";
  if (k === yest) return "Yesterday";
  const d = new Date(k + "T00:00:00");
  return isNaN(d) ? k : d.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
};
const timeOf = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" }); };


export function JobCommunications({ job }) {
  const { user } = useAuth();
  const isOffice = ["admin", "office", "super_admin", "quantity_surveyor"].includes(user.role);
  const [mailbox, setMailbox] = useState(null);
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [typeF, setTypeF] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [mb, cs] = await Promise.all([
        api.get(`/jobs/${job.id}/mailbox`),
        api.get(`/jobs/${job.id}/communications`, { params: { type: typeF || undefined, q: q || undefined } }),
      ]);
      setMailbox(mb.data);
      setItems(cs.data.items || []);
    } catch { /* mailbox errors surface via toast on actions */ } finally { setLoading(false); }
  };
  useEffect(() => { if (isOffice) load(); // eslint-disable-next-line
  }, [job.id, typeF]);
  useEffect(() => {
    if (!isOffice) return;
    const t = setTimeout(load, 350);
    return () => clearTimeout(t); // eslint-disable-next-line
  }, [q]);

  if (!isOffice) return null;

  const quarantined = items.filter((c) => c.status === "quarantined");
  const stored = items.filter((c) => c.status === "stored");
  const groups = stored.reduce((acc, c) => {
    const k = dayKey(c.received_at);
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});

  const copyAddr = () => {
    navigator.clipboard.writeText(mailbox?.address || "");
    toast.success("Project email copied");
  };
  const setPolicy = async (v) => {
    try {
      const { data } = await api.patch(`/jobs/${job.id}/mailbox`, { sender_policy: v });
      setMailbox(data);
      toast.success("Sender policy updated");
    } catch { toast.error("Failed to update policy"); }
  };
  const openComm = async (c) => {
    try { const { data } = await api.get(`/communications/${c.id}`); setSel(data); }
    catch { toast.error("Couldn't open communication"); }
  };
  const review = async (c, action) => {
    try {
      await api.post(`/communications/${c.id}/review`, { action });
      toast.success({ approve: "Added to job", trust: "Sender trusted", block: "Sender blocked", delete: "Deleted" }[action]);
      if (sel?.id === c.id && (action === "delete" || action === "block")) setSel(null);
      load();
    } catch { toast.error("Action failed"); }
  };

  return (
    <section className="hard-card p-5 min-w-0" data-testid="job-communications">
      <div className="label-tag mb-2 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Project communications</div>

      {/* Project email panel */}
      <div className="rounded-lg border border-zinc-300 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03] p-3.5">
        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Project email address</div>
        <p className="text-[11px] text-zinc-500 mt-0.5">CC or forward project correspondence to this unique address to automatically save it against this job.</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <code className="text-[12px] font-mono bg-white dark:bg-black/40 border border-zinc-300 dark:border-white/10 rounded-md px-2.5 py-1.5 break-all min-w-0"
                data-testid="project-email-address">{mailbox?.address || "…"}</code>
          <Button size="sm" variant="outline" onClick={copyAddr} className="h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider" data-testid="copy-project-email">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Who can send?</span>
          <Select value={mailbox?.sender_policy || "contacts_users"} onValueChange={setPolicy}>
            <SelectTrigger className="h-8 w-[250px] rounded-lg text-xs" data-testid="sender-policy-select"><SelectValue /></SelectTrigger>
            <SelectContent>{POLICIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <ManageSenders job={job} mailbox={mailbox} onSaved={setMailbox} />
        </div>
      </div>

      {/* Quarantine */}
      {quarantined.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/20 p-3" data-testid="comms-quarantine">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
            <ShieldAlert className="w-3.5 h-3.5" /> {quarantined.length} email{quarantined.length > 1 ? "s" : ""} need review — unknown sender
          </div>
          {quarantined.map((c) => (
            <div key={c.id} className="mt-2 flex items-center gap-2 flex-wrap text-xs">
              <span className="font-bold truncate">{c.subject}</span>
              <span className="text-zinc-500 truncate">{c.sender_email}</span>
              <span className="flex gap-1.5 ml-auto">
                <button onClick={() => review(c, "approve")} className="text-[10px] font-bold uppercase text-emerald-600 hover:underline" data-testid={`q-approve-${c.id}`}>Add to job</button>
                <button onClick={() => review(c, "trust")} className="text-[10px] font-bold uppercase text-sky-600 hover:underline">Trust sender</button>
                <button onClick={() => review(c, "block")} className="text-[10px] font-bold uppercase text-red-500 hover:underline">Block</button>
                <button onClick={() => review(c, "delete")} className="text-[10px] font-bold uppercase text-zinc-500 hover:underline">Delete</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        {TYPE_FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setTypeF(v)} data-testid={`comms-filter-${v || "all"}`}
            className={`h-7 px-2.5 rounded-md text-[10.5px] font-bold border transition-colors ${typeF === v ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`}>
            {l}
          </button>
        ))}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search emails, subjects, people…"
                 className="h-8 pl-8 rounded-lg text-xs" data-testid="comms-search" />
        </div>
      </div>

      {/* Empty state */}
      {!loading && stored.length === 0 && quarantined.length === 0 && (
        <div className="mt-4 text-center py-8 border border-dashed border-zinc-300 dark:border-white/10 rounded-lg" data-testid="comms-empty">
          <Mail className="w-6 h-6 mx-auto text-zinc-400" />
          <p className="text-sm font-bold mt-2">No project communications yet</p>
          <p className="text-xs text-zinc-500 mt-1">CC or forward an email to the project address above and it will appear here.</p>
        </div>
      )}

      {/* List + viewer */}
      {stored.length > 0 && (
        <div className="mt-3 grid md:grid-cols-[minmax(260px,340px)_1fr] gap-3 min-w-0">
          <div className={`border border-zinc-200 dark:border-white/10 rounded-lg overflow-hidden max-h-[520px] overflow-y-auto ${sel ? "hidden md:block" : ""}`} data-testid="comms-list">
            {Object.keys(groups).sort().reverse().map((k) => (
              <div key={k}>
                <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500 bg-zinc-100 dark:bg-white/[0.04] sticky top-0">{dayLabel(k)}</div>
                {groups[k].map((c) => (
                  <button key={c.id} onClick={() => openComm(c)} data-testid={`comm-row-${c.id}`}
                    className={`w-full text-left px-3 py-2 border-b border-zinc-100 dark:border-white/[0.05] hover:bg-zinc-50 dark:hover:bg-white/[0.03] ${sel?.id === c.id ? "bg-[#FF5F15]/5 shadow-[inset_2px_0_0_#FF5F15]" : ""}`}>
                    <span className="flex items-center gap-1.5">
                      <span className="text-[12.5px] font-bold truncate flex-1">{c.subject}</span>
                      {c.attachments?.length > 0 && <Paperclip className="w-3 h-3 text-zinc-400 shrink-0" />}
                      <span className="text-[10px] font-mono text-zinc-500 shrink-0">{timeOf(c.received_at)}</span>
                    </span>
                    <span className="block text-[11px] text-zinc-500 truncate">{c.sender_name}{c.sender_company ? ` — ${c.sender_company}` : ""}</span>
                    {c.preview && <span className="block text-[10.5px] text-zinc-400 truncate">{c.preview}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className={`min-w-0 ${sel ? "" : "hidden md:block"}`}>
            {sel ? <CommViewer key={sel.id} comm={sel} onSelect={openComm} onBack={() => setSel(null)} onChanged={(c) => { openComm(c); load(); }} />
              : <div className="h-full min-h-[200px] border border-dashed border-zinc-300 dark:border-white/10 rounded-lg flex items-center justify-center text-xs text-zinc-500">Select a communication</div>}
          </div>
        </div>
      )}
    </section>
  );
}

function ManageSenders({ job, mailbox, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [domain, setDomain] = useState("");
  if (!mailbox) return null;
  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/jobs/${job.id}/mailbox`, patch);
      onSaved(data);
    } catch { toast.error("Failed to save"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider" data-testid="manage-senders-btn">Manage allowed senders</Button>
      </DialogTrigger>
      <DialogContent className="rounded-lg max-w-md">
        <DialogHeader><DialogTitle className="font-display uppercase">Allowed senders</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <div className="label-tag mb-1">Approved email addresses</div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {(mailbox.allowed_senders || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-md px-2 py-0.5 text-xs font-mono">
                  {e}<button onClick={() => save({ allowed_senders: mailbox.allowed_senders.filter((x) => x !== e) })}><X className="w-3 h-3 text-zinc-500" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.co.nz" className="h-8 text-xs rounded-lg" data-testid="add-sender-input" />
              <Button size="sm" className="h-8 rounded-lg bg-[#FF5F15] hover:bg-[#E65100]" data-testid="add-sender-btn"
                onClick={() => { if (email.includes("@")) { save({ allowed_senders: [...(mailbox.allowed_senders || []), email.trim().toLowerCase()] }); setEmail(""); } }}>Add</Button>
            </div>
          </div>
          <div>
            <div className="label-tag mb-1">Approved domains</div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {(mailbox.allowed_domains || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-md px-2 py-0.5 text-xs font-mono">
                  @{d}<button onClick={() => save({ allowed_domains: mailbox.allowed_domains.filter((x) => x !== d) })}><X className="w-3 h-3 text-zinc-500" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="beca.com" className="h-8 text-xs rounded-lg" data-testid="add-domain-input" />
              <Button size="sm" variant="outline" className="h-8 rounded-lg" data-testid="add-domain-btn"
                onClick={() => { if (domain.includes(".")) { save({ allowed_domains: [...(mailbox.allowed_domains || []), domain.trim().toLowerCase()] }); setDomain(""); } }}>Add</Button>
            </div>
          </div>
          {(mailbox.blocked_senders || []).length > 0 && (
            <div>
              <div className="label-tag mb-1">Blocked senders</div>
              <div className="flex flex-wrap gap-1.5">
                {mailbox.blocked_senders.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 bg-red-50 dark:bg-red-950/30 border border-red-300/50 rounded-md px-2 py-0.5 text-xs font-mono text-red-500">
                    {e}<button onClick={() => save({ blocked_senders: mailbox.blocked_senders.filter((x) => x !== e) })}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-zinc-500">Company users and project contacts (Clients with matching emails) are recognised automatically under the default policy.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
